import React from 'react';
import {
	Area,
	AreaChart,
	Label,
	Legend,
	Tooltip,
	XAxis,
	YAxis,
	ReferenceDot
} from "recharts";

import "./RiskArtificialInflationGraph.css"
import {numberWithCommas, dollarFormat, getStatType, boolToYesNo} from "../../../util";

const getDateTimestamp = (dateArray) => {
	const [year, month, day] = dateArray.split("-");

	return new Date(year, month - 1, day)
		.setHours(0, 0, 0, 0);
};

const renderAdverseCorporateEventDots = (graphData, adverseCorporateEvents) => {
	if (!graphData || graphData.length === 0 || !adverseCorporateEvents) {
		return null;
	}

	const getClosestGraphPoint = (eventDateArray) => {
		const eventTime = getDateTimestamp(eventDateArray);

		return graphData.reduce((closest, point) => {
			const currentDiff = Math.abs(point.t - eventTime);
			const closestDiff = Math.abs(closest.t - eventTime);

			return currentDiff < closestDiff ? point : closest;
		}, graphData[0]);
	};

	return adverseCorporateEvents.map((event, index) => {
		const point = getClosestGraphPoint(event.date);

		if (!point) {
			return null;
		}
		let fillColor = 'orange';
		let strokeColor = 'darkorange';

		if (event.statTypeNum === 3) {
			fillColor = 'red';
			strokeColor = 'darkred';
		}
		else if (event.statTypeNum === 2) {
			fillColor = 'pink';
			strokeColor = 'deeppink';
		}
		return (
			<ReferenceDot
				key={`adverse-corporate-event-${index}`}
				x={point.t}
				y={point.closingPriceLessInflation}
				yAxisId="left"
				r={4}
				fill={fillColor}
				stroke={strokeColor}
			/>
		);
	});
};

const CustomTooltip = (props) => {
	const payloadItems = [];

	for (let payloadIndex in props.payload) {
		const payload = props.payload[payloadIndex];

		let value = payload.value;
		if (payload.dataKey === 'volume') {
			value = value.toFixed(2);
		}
		if (payload.dataKey === 'closingPriceLessInflation') {
			value = '$' + value;
		}
		if (payload.dataKey === 'closingPrice') {
			value = '$' + value;
		}

		payloadItems.push(
			<li key={payload.name} className="recharts-tooltip-item"
				style={{display: 'block', paddingTop: '4px', paddingBottom: '4px'}}>
				<span className="recharts-tooltip-item-name">{payload.name}</span>
				<span className="recharts-tooltip-item-separator"> : </span>
				<span
					className="recharts-tooltip-item-value">{payload.name === 'Volume' ? numberWithCommas(value) : value}</span>
			</li>
		);
	}

	const tooltipDate = new Date(props.label)
		.setHours(0, 0, 0, 0);

	const adverseCorporateEvent =
		props.adverseCorporateEvents.find(event => {
			const eventDate = getDateTimestamp(event.date);

			return eventDate === tooltipDate;
		});

	return (
		<div style={{
			margin: '0px',
			padding: '5px',
			backgroundColor: 'black',
			color: 'white',
			fontFamily: 'GothamLight',
			lineHeight: 1.2
		}}>
			<p className={"recharts-tooltip-label"}>{new Date(props.label).toLocaleDateString()}</p>
			<ul style={{padding: '0px', margin: '0px'}}>
				{payloadItems}

				{
					adverseCorporateEvent &&
					<li
						className="recharts-tooltip-item"
						style={{
							display: 'block',
							paddingTop: '4px',
							paddingBottom: '4px'
						}}
					>
						<span className="recharts-tooltip-item-name">
							ACE Type
						</span>
						<span className="recharts-tooltip-item-separator"> : </span>
						<span className="recharts-tooltip-item-value" style={{color: adverseCorporateEvent.statTypeNum === 3?
							 'red': ( adverseCorporateEvent.statTypeNum === 2? 
							 	'pink' :
								'orange'
							 )
						}}>
							{getStatType(adverseCorporateEvent.statTypeNum)}
						</span>
						<br />
						<span className="recharts-tooltip-item-name">
							Severe
						</span>
						<span className="recharts-tooltip-item-separator"> : </span>
						<span className="recharts-tooltip-item-value" style={{color: adverseCorporateEvent.vFiltersPass ?
								'red': 
								'orange'
						}}>
							{boolToYesNo(adverseCorporateEvent.vFiltersPass)}
						</span>

					</li>
				}
			</ul>
		</div>
	);
};

const RiskArtificialInflationGraphComponent = (props) => (
	// <ResponsiveContainer width={1200} height={400}>
	<AreaChart data={props.graphData}
		width={1200} height={400}
		margin={{top: 15, right: 30, left: 20, bottom: 5}}>
		<XAxis dataKey='t'
			stroke="white"
			type='number'
			domain={[
				Math.min.apply(Math, props.graphData.map(tick => tick.t)),
				Math.max.apply(Math, props.graphData.map(tick => tick.t))
			]}
			tickFormatter={x => {
				return new Date(x).toLocaleDateString();
			}}
			ticks={props.tickDates}
			angle={-45}
			textAnchor="end"
			height={90}>
			<Label value={'Date'} dy={22}/>
		</XAxis>
		<YAxis yAxisId="left"
			name="Price"
			stroke="white"
			tickFormatter={dollarFormat}>
			<Label value='Stock Closing Price' angle={-90} position="insideLeft"/>
		</YAxis>
		<YAxis yAxisId="right"
			name="Volume"
			orientation="right"
			tickFormatter={numberWithCommas}
			stroke="white">
			<Label value="Vol (000s)" angle={-90} position="right"/>
		</YAxis>

		<Tooltip
			content={
				<CustomTooltip
					adverseCorporateEvents={props.adverseCorporateEvents}
				/>
			}
		/>
		<Legend
			wrapperStyle={{width:600, textAlign:"Left", align:"center", marginLeft: "25%"}}
			align="center"
			textAlign="Left"
			payload={[
				{
					value: "Potential \"But-For\" Stock Price",
					type: 'square',
					color: '#22aef0'
				},
				{
					value: "Actual Closing Stock Price",
					type: 'square',
					color: '#C00000'
				},
				{
					value: 'Volume',
					type: 'square',
					color: '#f0ec00'
				},
				{
					value: 'Type 1 Event',
					type: 'circle',
					color: 'orange'
				},
				{
					value: 'Type 2 Event',
					type: 'circle',
					color: 'pink'
				},
				{
					value: 'High-Risk Event',
					type: 'circle',
					color: 'red'
				}
			]}
		/>
		{props.caseRuleTypeId != null && props.caseRuleTypeId === 3 ?
			""
			:
			<Area yAxisId="left" type='monotone' dataKey='closingPrice' stackId="1"
				stroke='#C00000' name={props.isDandO === "true" ? "Actual Closing Stock Price" : "Alleged Inflated Stock Price"} fill='#C00000' isAnimationActive={false}/>
		}
		<Area yAxisId="left" type='monotone' dataKey='closingPriceLessInflation' stackId="3"
			stroke='#22aef0' name={props.isDandO === "true" ? "Potential \"But-For\" Stock Price" : "Stock Price"} fill='#22aef0' isAnimationActive={false}/>
		<Area yAxisId="right" type='monotone' dataKey='volume' stackId="2" name='Volume'
			stroke='#f0ec00' fill='#f0ec00' isAnimationActive={false}/>

		{renderAdverseCorporateEventDots(
			props.graphData,
			props.adverseCorporateEvents
		)}
	</AreaChart>
	// </ResponsiveContainer>
);

export default RiskArtificialInflationGraphComponent;


// WEBPACK FOOTER //
// ./src/common/components/RiskArtificialInflationGraph/RiskArtificialInflationGraphComponent.js